import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import SearchHero from "../components/SearchHero";
import ListingCard from "../components/ListingCard";
import { NewsletterSignup } from "../components/NewsletterSignup";
import {
    Zap,
    Shield,
    TrendingUp,
    Users,
    ArrowRight,
    Handshake,
    Wrench,
    Building2,
    Factory,
    ChevronRight,
    Gavel,
    KeyRound,
    Coins,
    Clock,
    History,
    BadgeEuro,
    Sun,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useTranslation } from "react-i18next";
import { getRecentlyViewed } from "../lib/recentlyViewed";
import { MotorcycleIcon as Motorcycle, ScooterIcon as Scooter } from "../components/icons/VehicleIcons";

export default function HomePage() {
    const [listings, setListings] = useState([]);
    const [topListings, setTopListings] = useState([]);
    const [favIds, setFavIds] = useState([]);
    const [stats, setStats] = useState({ motorcycles: 0, scooters: 0, bicycles: 0, dealers: 0 });
    const [spBranches, setSpBranches] = useState([]);
    const [recommended, setRecommended] = useState({ items: [], signals: null });
    const [auctions, setAuctions] = useState([]);
    const [rentals, setRentals] = useState([]);
    const [pawns, setPawns] = useState([]);
    const [recentlyViewed, setRecentlyViewed] = useState([]);
    const { user } = useAuth();
    const { t } = useTranslation();

    const attributionFiredRef = useRef(false);
    useEffect(() => {
        if (!attributionFiredRef.current) {
            try {
                const params = new URLSearchParams(window.location.search);
                const ref = params.get("ref");
                if (ref && /^H_[a-z0-9-]{6,40}$/i.test(ref)) {
                    attributionFiredRef.current = true;
                    api.post("/marketing/attribute", { code: ref, kind: "pageview" }).catch(() => {});
                }
            } catch { /* noop */ }
        }
        (async () => {
            // Limit recent-listings to the motorcycle segment so the "Aktuelle Inserate"
            // strip on the homepage stays moto/scooter-focused as requested.
            const [r1, r2, rTop] = await Promise.all([
                api.get("/listings?category=motorcycle&limit=6"),
                api.get("/stats"),
                api.get("/listings?featured=true&category=motorcycle&limit=4").catch(() => ({ data: [] })),
            ]);
            setListings(r1.data);
            setStats(r2.data);
            setTopListings(rTop.data || []);
            // Aktuelle Auktionen + Vermietungs-Angebote (Bereiche erscheinen nur bei Treffern)
            api.get("/listings?sale_type=auction&limit=8")
                .then((r) => {
                    const now = Date.now();
                    const active = (r.data || []).filter((a) => !a.auction_end || new Date(a.auction_end).getTime() > now);
                    setAuctions(active.slice(0, 4));
                })
                .catch(() => {});
            api.get("/listings?sale_type=rental&limit=4")
                .then((r) => setRentals(r.data || []))
                .catch(() => {});
            api.get("/listings?sale_type=pawn&limit=4")
                .then((r) => setPawns(r.data || []))
                .catch(() => {});
            if (user && user !== false) {
                try {
                    const f = await api.get("/favorites/ids");
                    setFavIds(f.data);
                } catch (e) {
                    console.error("Failed to load favorite ids", e);
                }
                // Phase 4.13 (L2) — Personalisierte Empfehlungen
                try {
                    const rec = await api.get("/listings/recommended-for-me?limit=8");
                    setRecommended(rec.data || { items: [], signals: null });
                } catch {
                    /* silent — empty state is fine */
                }
            }
        })();
    }, [user]);

    useEffect(() => {
        api.get("/service-providers/branches").then((r) => setSpBranches(r.data.items || [])).catch(() => {});
    }, []);

    useEffect(() => {
        setRecentlyViewed(getRecentlyViewed().slice(0, 5));
    }, []);

    const toggleFav = async (id) => {
        if (!user || user === false) {
            window.location.href = "/login";
            return;
        }
        const { data } = await api.post(`/favorites/${id}`);
        setFavIds((prev) => (data.favorited ? [...prev, id] : prev.filter((x) => x !== id)));
    };

    return (
        <div data-testid="home-page">
            {/* Hero — „Projekt Zukunft"-Stil: Deine Zweiradhändler für dein Bike.
                Layout & Optik an die Landingpage (zweiradhub) angelehnt, Primär-CTA
                verlinkt dorthin. */}
            <section className="relative overflow-hidden bg-gradient-to-b from-[#FFF7ED] via-[#FFEDD5] to-[#FFE4CC]" data-testid="home-hero">
                {/* Grid-Textur */}
                <div
                    className="absolute inset-0 opacity-40"
                    style={{ backgroundImage: "linear-gradient(rgba(17,24,39,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(17,24,39,0.06) 1px, transparent 1px)", backgroundSize: "48px 48px" }}
                    aria-hidden="true"
                />
                {/* Weicher Orange-Glow */}
                <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[60rem] h-[40rem] bg-[#F97316]/15 rounded-full blur-3xl" aria-hidden="true" />
                <div className="relative max-w-6xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-32 md:pb-40 text-center">
                    <div className="inline-flex items-center gap-2.5 text-[#EA580C] text-base md:text-lg font-bold uppercase tracking-[0.2em] mb-6" data-testid="hero-kicker">
                        <Sun className="w-5 h-5" /> {t("home.hero_kicker", "Projekt Zukunft")} <span className="text-[#EA580C]/50">//</span> {t("home.hero_kicker2", "Die Zweiradhändler")}
                        <span className="w-2 h-2 rounded-full bg-[#EA580C] animate-pulse" />
                    </div>
                    <h1 className="font-display font-black tracking-tight leading-[0.88]">
                        <span className="block text-[#111827] text-5xl sm:text-7xl lg:text-8xl">{t("home.hero_h1_your", "DEINE")}</span>
                        <span className="block text-transparent bg-clip-text bg-gradient-to-b from-[#F97316] to-[#FB923C] text-[2rem] leading-none sm:text-6xl lg:text-8xl">{t("home.hero_h1_dealers", "ZWEIRADHÄNDLER")}</span>
                    </h1>
                    <div className="flex items-center justify-center gap-4 my-6" data-testid="hero-claim">
                        <span className="h-px w-10 md:w-16 bg-gray-400/70" />
                        <span className="font-display text-2xl md:text-4xl font-bold text-[#111827]">{t("home.hero_claim", "für dein Bike.")}</span>
                        <span className="h-px w-10 md:w-16 bg-gray-400/70" />
                    </div>
                    {/* Support-Callout: Werkstätten/Händler sterben aus — mittig zwischen Claim & Suche */}
                    <div
                        className="mt-10 max-w-3xl mx-auto px-6 py-6 sm:px-8 sm:py-7 text-center"
                        data-testid="hero-support-callout"
                    >
                        <p className="text-sm md:text-base text-[#111827] leading-relaxed">
                            {t("home.support_text", "Unsere Motorradwerkstätten verschwinden. Wer repariert morgen unsere Bikes, wenn Betriebe wegen Personalmangel und Kostenflut schließen? Lange Wartezeiten und hohe Rechnungen sind erst der Anfang.")} <span className="font-bold text-[#111827]">{t("home.support_text_bold", "Wir müssen jetzt handeln, um das Handwerk zu retten!")}</span>
                        </p>
                        <div className="mt-5 flex justify-center">
                            <a
                                href="https://twowheels.info/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-full transition-colors shadow-lg shadow-[#F97316]/30"
                                data-testid="hero-support-btn"
                            >
                                <Handshake className="w-3.5 h-3.5 text-white" /> {t("home.support_btn", "Als Biker das Projekt unterstützen")}
                                <ArrowRight className="w-3.5 h-3.5" />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Search widget (overlapping) — etwas höher Richtung Hero-CTA gezogen */}
            <section className="relative -mt-40 md:-mt-44 z-10">
                <div className="max-w-5xl mx-auto px-4 md:px-8">
                    <SearchHero />
                </div>
            </section>

            {/* Stats strip — moto / roller / händler / inserate-per-day */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-16">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 rounded-sm overflow-hidden border border-gray-200">
                    {[
                        { label: t("home.stats_motorcycles"), value: stats.motorcycles || 0, icon: Motorcycle },
                        { label: t("home.stats_scooters"), value: stats.scooters || 0, icon: Scooter },
                        { label: t("home.stats_verified_dealers"), value: stats.dealers || 0, icon: Shield },
                        { label: t("home.stats_listings_per_day"), value: "1.200+", icon: TrendingUp },
                    ].map((s) => (
                        <div key={s.label} className="bg-white p-6 flex items-center gap-4" data-testid={`stat-${s.label}`}>
                            <div className="w-12 h-12 bg-[#FFCC00] flex items-center justify-center rounded-sm">
                                <s.icon className="w-6 h-6 text-[#111827]" strokeWidth={2.5} />
                            </div>
                            <div>
                                <div className="font-display font-black text-2xl font-mono-num">{s.value}</div>
                                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">{s.label}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA — Fahrzeug-Ankauf: „Wir wollen dein Bike" (kompakt) */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-16" data-testid="home-ankauf-cta">
                <div className="relative overflow-hidden rounded-sm bg-[#DC2626] text-white">
                    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "22px 22px" }} />
                    <div className="relative px-5 md:px-8 py-5 md:py-7 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="max-w-2xl">
                            <div className="inline-flex items-center gap-1.5 bg-white text-[#DC2626] px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider mb-2">
                                <BadgeEuro className="w-3 h-3" /> {t("home.ankauf_badge", "Fahrzeug-Ankauf")}
                            </div>
                            <h2 className="font-display text-xl md:text-2xl font-black tracking-tight leading-tight mb-1.5">
                                {t("home.ankauf_title", "Wir wollen dein Bike.")}
                            </h2>
                            <p className="text-sm text-white/90">
                                {t("home.ankauf_text", "Verkaufe dein Fahrzeug höchstbietend — geprüfte Händler bieten um dein Motorrad oder deinen Roller. Kostenlos & unverbindlich.")}
                            </p>
                        </div>
                        <Link to="/ankauf" data-testid="home-ankauf-cta-btn" className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#111827] hover:bg-black text-white font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm transition-colors">
                            {t("home.ankauf_btn", "Jetzt verkaufen")} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Aktuelle Auktionen — erscheint nur, wenn laufende Auktionen existieren */}
            {auctions.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 md:px-8 mt-24" data-testid="home-auctions-section">
                    <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
                        <div>
                            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#111827] text-[#FFCC00] px-2 py-1 rounded-sm mb-2">
                                <Gavel className="w-3.5 h-3.5" /> {t("home.auctions_badge", "Live-Auktionen")}
                            </div>
                            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">{t("home.auctions_title", "Aktuelle Auktionen")}</h2>
                        </div>
                        <Link to="/auctions" className="hidden md:flex items-center gap-1 text-sm font-bold border-b-2 border-[#111827] hover:gap-2 transition-all" data-testid="see-all-auctions">
                            {t("home.auctions_all", "Alle Auktionen")} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {auctions.map((l) => (
                            <ListingCard key={l.id} listing={l} onToggleFavorite={toggleFav} isFavorited={favIds.includes(l.id)} vertical />
                        ))}
                    </div>
                </section>
            )}

            {/* Fahrzeuge zur Vermietung — erscheint nur, wenn Mietangebote existieren */}
            {rentals.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 md:px-8 mt-24" data-testid="home-rental-section">
                    <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
                        <div>
                            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#0369A1] text-white px-2 py-1 rounded-sm mb-2">
                                <KeyRound className="w-3.5 h-3.5" /> {t("home.rentals_badge", "Vermietung")}
                            </div>
                            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">{t("home.rentals_title", "Fahrzeuge zur Vermietung")}</h2>
                        </div>
                        <Link to="/search?category=motorcycle&sale_type=rental" className="hidden md:flex items-center gap-1 text-sm font-bold border-b-2 border-[#111827] hover:gap-2 transition-all" data-testid="see-all-rentals">
                            {t("home.rentals_all", "Alle Mietangebote")} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {rentals.map((l) => (
                            <ListingCard key={l.id} listing={l} onToggleFavorite={toggleFav} isFavorited={favIds.includes(l.id)} vertical />
                        ))}
                    </div>
                </section>
            )}

            {/* Pfand / Refinanzieren — erscheint nur, wenn Pfand-Angebote existieren */}
            {pawns.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 md:px-8 mt-24" data-testid="home-pawn-section">
                    <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
                        <div>
                            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#7C3AED] text-white px-2 py-1 rounded-sm mb-2">
                                <Coins className="w-3.5 h-3.5" /> {t("home.pawn_badge", "Pfand / Refinanzieren")}
                            </div>
                            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">{t("home.pawn_title", "Pfand & Refinanzierung")}</h2>
                        </div>
                        <Link to="/search?category=motorcycle&sale_type=pawn" className="hidden md:flex items-center gap-1 text-sm font-bold border-b-2 border-[#111827] hover:gap-2 transition-all" data-testid="see-all-pawns">
                            {t("home.pawn_all", "Alle Pfand-Angebote")} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {pawns.map((l) => (
                            <ListingCard key={l.id} listing={l} onToggleFavorite={toggleFav} isFavorited={favIds.includes(l.id)} vertical />
                        ))}
                    </div>
                </section>
            )}

            {/* Zuletzt angesehen — persönlich (localStorage), erscheint nur wenn vorhanden */}
            {recentlyViewed.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 md:px-8 mt-24" data-testid="home-recently-viewed">
                    <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
                        <div>
                            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-gray-900 text-white px-2 py-1 rounded-sm mb-2">
                                <History className="w-3.5 h-3.5" /> {t("home.recent_badge", "Zuletzt angesehen")}
                            </div>
                            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">{t("home.recent_title", "Deine zuletzt angesehenen Fahrzeuge")}</h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                        {recentlyViewed.map((l) => (
                            <ListingCard key={l.id} listing={l} onToggleFavorite={toggleFav} isFavorited={favIds.includes(l.id)} vertical compact />
                        ))}
                    </div>
                </section>
            )}

            {/* Top-Inserate (Premium-Slot) */}
            {topListings.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 md:px-8 mt-24" data-testid="home-top-listings-section">
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#FFCC00] text-[#111827] px-2 py-1 rounded-sm mb-2">
                                <Zap className="w-3.5 h-3.5" /> {t("home.top_badge", "Top-Inserate")}
                            </div>
                            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">{t("home.top_title", "Aktuell besonders empfohlen")}</h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {topListings.map((l) => (
                            <ListingCard
                                key={l.id}
                                listing={l}
                                onToggleFavorite={toggleFav}
                                isFavorited={favIds.includes(l.id)}
                                vertical
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* Phase 4.13 (L2) — Personalisierte Empfehlungen */}
            {user && user !== false && recommended.items.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 md:px-8 mt-24" data-testid="recommended-section">
                    <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
                        <div>
                            <div className="text-xs font-bold uppercase tracking-wider text-violet-700 mb-2 flex items-center gap-1">
                                ✨ {t("home.foryou_badge", "Für dich ausgewählt")}
                            </div>
                            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">{t("home.foryou_title", "Inserate, die zu dir passen")}</h2>
                            {recommended.signals?.preferred_brands?.length > 0 && (
                                <p className="text-xs text-gray-600 mt-1">
                                    {t("home.foryou_based", "Basierend auf deinen Suchen:")} {recommended.signals.preferred_brands.slice(0, 3).join(", ")}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="recommended-grid">
                        {recommended.items.slice(0, 8).map((l) => (
                            <ListingCard
                                key={l.id}
                                listing={l}
                                onToggleFavorite={toggleFav}
                                isFavorited={favIds.includes(l.id)}
                                vertical
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* Aktuelle Inserate (motorcycle segment) */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-24">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-[#111827] mb-2">{t("home.section_recommendations")}</div>
                        <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">{t("home.section_recent_listings")}</h2>
                    </div>
                    <Link to="/search?category=motorcycle" className="hidden md:flex items-center gap-1 text-sm font-bold border-b-2 border-[#111827] hover:gap-2 transition-all" data-testid="see-all-link">
                        {t("home.see_all")} <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {listings.slice(0, 2).map((l) => (
                        <ListingCard
                            key={l.id}
                            listing={l}
                            onToggleFavorite={toggleFav}
                            isFavorited={favIds.includes(l.id)}
                        />
                    ))}
                </div>
            </section>

            {/* Become a seller CTA (kompakt — ~50% kleiner) */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-sm overflow-hidden border border-gray-200">
                    <div className="bg-[#FFCC00] p-5 md:p-7">
                        <div className="text-[10px] font-bold uppercase tracking-wider mb-2 inline-block bg-[#111827] text-white px-2 py-0.5 rounded-sm">{t("home.seller_badge")}</div>
                        <h2 className="font-display text-xl md:text-2xl font-black tracking-tight mb-2 leading-tight">{t("home.seller_title")}</h2>
                        <p className="text-[#111827]/80 text-sm mb-4 leading-relaxed">
                            {t("home.seller_subtitle")}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2.5">
                            <Link
                                to="/register?role=b2b"
                                className="inline-flex items-center justify-center gap-2 bg-[#111827] hover:bg-black text-white px-4 py-2 rounded-sm font-bold text-xs uppercase tracking-wider transition-colors"
                                data-testid="cta-dealer"
                            >
                                <Users className="w-3.5 h-3.5" /> {t("home.seller_cta_dealer")}
                            </Link>
                            <Link
                                to="/register?role=b2c"
                                className="inline-flex items-center justify-center gap-2 border-2 border-[#111827] text-[#111827] px-4 py-2 rounded-sm font-bold text-xs uppercase tracking-wider hover:bg-[#111827] hover:text-white transition-colors"
                                data-testid="cta-private"
                            >
                                {t("home.seller_cta_private")}
                            </Link>
                        </div>
                    </div>
                    <div className="bg-[#111827] text-white p-5 md:p-7 relative overflow-hidden">
                        <img
                            src="https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover opacity-30"
                        />
                        <div className="relative">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-[#FFCC00] mb-2">{t("home.how_it_works")}</div>
                            <ol className="space-y-2.5">
                                {[t("home.step_create_account"), t("home.step_create_listing"), t("home.step_inbox")].map((step, i) => (
                                    <li key={step} className="flex gap-3">
                                        <div className="w-6 h-6 text-sm bg-[#FFCC00] text-[#111827] font-display font-black flex items-center justify-center rounded-sm flex-shrink-0 font-mono-num">
                                            {i + 1}
                                        </div>
                                        <div className="pt-0.5 text-sm text-white/90">{step}</div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                </div>
            </section>

            {/* Dienstleister-CTA (kompakt — ~50% kleiner) */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-16" data-testid="home-service-provider-cta">
                <div className="relative rounded-sm overflow-hidden border border-gray-200 bg-white">
                    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-0">
                        <div className="p-5 md:p-7 bg-[#111827] text-white relative overflow-hidden">
                            <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#FFCC00]/10 rounded-full blur-3xl" aria-hidden="true" />
                            <div className="relative">
                                <div className="inline-flex items-center gap-2 bg-[#FFCC00] text-[#111827] px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider mb-2.5">
                                    <Handshake className="w-3 h-3" /> {t("home.sp_badge", "Dienstleister-Netzwerk")}
                                </div>
                                <h2 className="font-display text-xl md:text-2xl font-black tracking-tight leading-tight mb-2">
                                    {t("home.sp_title_a", "Sie sind")} <span className="text-[#FFCC00]">{t("home.sp_title_b", "Dienstleister")}</span> {t("home.sp_title_c", "rund ums Motorrad?")}
                                </h2>
                                <p className="text-white/80 text-sm mb-4 leading-relaxed max-w-xl">
                                    {t("home.sp_text", "Hol-/Bringservice, Transport, Zulassung, Versicherung, Garantien, Import — listen Sie Ihr Angebot kostenfrei in unserem Verzeichnis und werden Sie direkt von Händlern und Kunden gefunden.")}
                                </p>
                                <div className="flex flex-col sm:flex-row gap-2.5">
                                    <Link
                                        to="/register/service-provider"
                                        className="inline-flex items-center justify-center gap-2 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] px-4 py-2 rounded-sm font-bold text-xs uppercase tracking-wider transition-colors"
                                        data-testid="home-sp-register-cta"
                                    >
                                        <Wrench className="w-3.5 h-3.5" /> {t("home.sp_register_btn", "Jetzt kostenfrei eintragen")}
                                    </Link>
                                    <Link
                                        to="/login"
                                        className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white px-4 py-2 rounded-sm font-bold text-xs uppercase tracking-wider transition-colors"
                                        data-testid="home-sp-login-cta"
                                    >
                                        {t("home.sp_login_btn", "Bereits registriert? Anmelden")}
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <div className="p-5 md:p-6 bg-[#FFFBEB]/60">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-[#92400E] mb-2.5">{spBranches.length || 15} Branchen abgedeckt</div>
                            <div className="flex flex-wrap gap-1.5" data-testid="home-sp-branches">
                                {(spBranches.length ? spBranches : [
                                    { key: "hol_bring_regional", label: "Hol-/Bringservice regional" },
                                    { key: "transport_europa", label: "Transport europaweit" },
                                    { key: "zulassung_regional", label: "Zulassung regional" },
                                    { key: "versicherungsagentur", label: "Versicherungsagentur" },
                                    { key: "garantiegeber", label: "Garantiegeber" },
                                ]).slice(0, 12).map((b) => (
                                    <Link
                                        key={b.key}
                                        to={`/register/service-provider?branch=${encodeURIComponent(b.key)}`}
                                        className="inline-flex items-center px-2.5 py-1 bg-white border border-gray-300 hover:border-[#111827] hover:bg-[#FFCC00]/30 rounded-sm text-[11px] font-bold text-gray-700 transition-colors"
                                        data-testid={`home-sp-branch-${b.key}`}
                                    >
                                        {b.label}
                                    </Link>
                                ))}
                            </div>
                            <p className="mt-3 text-[11px] text-gray-600 leading-relaxed">
                                {t("home.sp_tip", "Tipp: Klicken Sie auf eine Branche, um Ihre Registrierung vorausgefüllt zu starten.")}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Fahrrad-Teaser entfernt (User-Wunsch Feb 2026) */}

            {/* Professioneller Zugang — konsistentes Karten-Grid (gleicher Stil wie RegisterPage) */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-16 mb-4" data-testid="home-pro-access">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-3 text-center">{t("home.pro_heading", "Professioneller Zugang")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 max-w-5xl mx-auto">
                    {[
                        { to: "/register?role=b2b", icon: Building2, title: t("home.pro_b2b_title", "Händler"), desc: t("home.pro_b2b_desc", "Autohaus · Motorrad-Händler · Fahrrad-Shop"), testid: "home-pro-link-b2b" },
                        { to: "/register?role=vermittler", icon: Handshake, title: t("home.pro_verm_title", "Vermittler / Verkäufer / Unterstützer"), desc: t("home.pro_verm_desc", "Tippgeber · Verkäufer · €-Punkte · Provision"), testid: "home-pro-link-vermittler" },
                        { to: "/register/service-provider", icon: Wrench, title: t("home.pro_sp_title", "Dienstleister / Lieferanten"), desc: t("home.pro_sp_desc", "Werkstatt · Bank · Versicherung · Lieferanten · …"), testid: "home-pro-link-sp" },
                        { to: "/register/service-provider?branch=hersteller", icon: Factory, title: t("home.pro_manu_title", "Hersteller / Importeure"), desc: t("home.pro_manu_desc", "Marken-Hersteller · Generalimporteure"), testid: "home-pro-link-hersteller" },
                        { to: "/register/service-provider?branch=clubs_vereine", icon: Users, title: t("home.pro_club_title", "Clubs / Vereine"), desc: t("home.pro_club_desc", "Motorrad-Club · Bike-Verein · Stammtisch"), testid: "home-pro-link-club" },
                    ].map(({ to, icon: Icon, title, desc, testid }) => (
                        <Link
                            key={testid}
                            to={to}
                            className="group flex flex-col gap-2 p-4 rounded-sm border-2 border-gray-200 hover:border-[#111827] hover:bg-[#FFCC00]/10 transition-colors bg-white text-left h-full"
                            data-testid={testid}
                        >
                            <div className="w-10 h-10 shrink-0 rounded-sm bg-[#FFCC00]/20 flex items-center justify-center group-hover:bg-[#FFCC00]/40 transition-colors">
                                <Icon className="w-5 h-5 text-[#111827]" />
                            </div>
                            <div className="text-sm font-bold text-[#111827]">{title}</div>
                            <div className="text-[11px] text-gray-500 leading-tight flex-1">{desc}</div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 group-hover:text-[#111827] inline-flex items-center gap-1 transition-colors">
                                {t("home.pro_register", "Registrieren")} <ChevronRight className="w-3 h-3" />
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            <NewsletterSignup />
        </div>
    );
}
